import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Activity, DollarSign, Briefcase, TrendingUp, ChevronDown, ChevronUp, Bell, BellOff } from 'lucide-react';
import api from '../api';

const NodeGraph = ({ accounts, changeRole, toggleMute }) => {
  const masters = accounts.filter(a => a.role === 'Master');
  const slaves = accounts.filter(a => a.role === 'Slave');
  const others = accounts.filter(a => !['Master', 'Slave'].includes(a.role));

  const AccountCard = ({ acc, type }) => {
    const isOnline = new Date(acc.last_seen).getTime() > Date.now() - 30000;
    const isMaster = type === 'master';
    const isSlave = type === 'slave';
    
    return (
      <div className="relative bg-slate-900/80 backdrop-blur rounded-2xl p-4 border border-slate-700/50 shadow-xl w-64 z-10 transition-transform hover:scale-105 group hover:border-slate-500/50">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${isMaster ? 'bg-indigo-500/20 text-indigo-400' : isSlave ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700/50 text-slate-400'}`}>
              <Activity className="w-4 h-4" />
            </div>
            <div>
              <span className="text-white font-bold block leading-tight">{acc.account_login}</span>
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{isOnline ? 'Online' : 'Offline'}</span>
            </div>
          </div>
          <div className={`w-2 h-2 rounded-full shadow-[0_0_8px] ${isOnline ? 'bg-emerald-500 shadow-emerald-500' : 'bg-slate-600 shadow-transparent'}`}></div>
        </div>
        
        <div className="flex items-center justify-between mt-4 bg-slate-950/50 p-2 rounded-lg border border-slate-800/50">
          <select
            value={acc.role}
            onChange={(e) => changeRole(acc.account_login, e.target.value)}
            className="bg-transparent text-xs font-semibold text-slate-300 outline-none cursor-pointer appearance-none"
          >
            <option value="Master" className="bg-slate-800">Master</option>
            <option value="Slave" className="bg-slate-800">Slave</option>
            <option value="Notifier" className="bg-slate-800">Notifier</option>
            <option value="Unassigned" className="bg-slate-800">Unassigned</option>
          </select>

          <div 
            onClick={() => toggleMute(acc.account_login, acc.mute_telegram)}
            className={`w-10 h-5 flex items-center rounded-full p-0.5 cursor-pointer transition-colors border ${!acc.mute_telegram ? 'bg-emerald-500/20 border-emerald-500/50' : 'bg-slate-800 border-slate-700'}`}
            title={acc.mute_telegram ? "Unmute Notifications" : "Mute Notifications"}
          >
            <div className={`w-4 h-4 rounded-full shadow-md transform transition-transform ${!acc.mute_telegram ? 'translate-x-5 bg-emerald-400 shadow-emerald-500/50' : 'translate-x-0 bg-slate-500 shadow-none'}`}></div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      <div className="flex justify-center items-stretch py-12 px-4 relative">
        {/* Left: Masters */}
        <div className="flex flex-col justify-center gap-8 z-10">
          {masters.map(m => (
            <div key={m.account_login} className="flex items-center">
              <AccountCard acc={m} type="master" />
              <div className="w-16 h-[2px] border-t-2 border-dashed border-indigo-500/50"></div>
            </div>
          ))}
          {masters.length === 0 && (
            <div className="flex items-center">
              <div className="w-64 h-24 border-2 border-dashed border-slate-700 rounded-2xl flex items-center justify-center text-slate-500 font-medium">No Masters</div>
              <div className="w-16 h-[2px] border-t-2 border-dashed border-slate-700/50"></div>
            </div>
          )}
        </div>

        {/* Center: Hub */}
        <div className="relative flex items-center justify-center z-20 -mx-[2px]">
          <div className="absolute top-10 bottom-10 left-1/2 w-[2px] -ml-[1px] border-l-2 border-dashed border-orange-500/40"></div>
          <div className="w-20 h-20 bg-slate-900 rounded-3xl rotate-45 flex items-center justify-center shadow-[0_0_50px_rgba(249,115,22,0.3)] border border-orange-500/50 z-30">
            <div className="-rotate-45 text-transparent bg-clip-text bg-gradient-to-br from-orange-400 to-orange-600 font-black text-5xl tracking-tighter shadow-orange-500 drop-shadow-[0_0_15px_rgba(249,115,22,0.8)]">X</div>
          </div>
        </div>

        {/* Right: Slaves */}
        <div className="flex flex-col justify-center gap-8 z-10">
          {slaves.map(s => (
            <div key={s.account_login} className="flex items-center">
              <div className="w-16 h-[2px] border-t-2 border-dashed border-emerald-500/50"></div>
              <AccountCard acc={s} type="slave" />
            </div>
          ))}
          {slaves.length === 0 && (
            <div className="flex items-center">
              <div className="w-16 h-[2px] border-t-2 border-dashed border-slate-700/50"></div>
              <div className="w-64 h-24 border-2 border-dashed border-slate-700 rounded-2xl flex items-center justify-center text-slate-500 font-medium">No Slaves</div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom: Others */}
      {others.length > 0 && (
        <div className="mt-8 pt-8 border-t border-slate-800/50">
          <h4 className="text-slate-500 text-sm font-bold uppercase tracking-widest mb-6 text-center">Unassigned & Notifiers</h4>
          <div className="flex flex-wrap justify-center gap-6">
            {others.map(o => <AccountCard key={o.account_login} acc={o} type="other" />)}
          </div>
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const [trades, setTrades] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTradeId, setExpandedTradeId] = useState(null);

  useEffect(() => {
    fetchTrades();
    fetchAccounts();
    const interval = setInterval(fetchAccounts, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/users/accounts');
      setAccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch accounts', error);
    }
  };

  const toggleMute = async (account_login, currentMuteStatus) => {
    try {
      await api.put(`/users/accounts/${account_login}/mute`, { mute_telegram: !currentMuteStatus });
      fetchAccounts(); // Refresh the list
    } catch (error) {
      console.error('Failed to toggle mute status', error);
    }
  };

  const changeRole = async (account_login, newRole) => {
    try {
      await api.put(`/users/accounts/${account_login}/role`, { role: newRole });
      fetchAccounts(); // Refresh the list
    } catch (error) {
      console.error('Failed to change role', error);
    }
  };

  const fetchTrades = async () => {
    try {
      setLoading(true);
      const response = await api.get('/trades');
      setTrades(response.data);
    } catch (error) {
      console.error('Failed to fetch trades', error);
    } finally {
      setLoading(false);
    }
  };

  const openTrades = trades.filter(t => t.status === 'Open');
  const closedTrades = trades.filter(t => t.status === 'Closed');
  const totalProfit = closedTrades.reduce((sum, t) => sum + (Number(t.profit) || 0), 0);

  if (loading) {
    return <div className="text-white text-center py-20">Loading Dashboard...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Trades Card */}
        <div className="bg-slate-800/50 backdrop-blur-md rounded-2xl p-6 border border-slate-700/50 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-slate-400 font-medium">Total Trades</h3>
            <div className="p-2 bg-indigo-500/10 text-indigo-400 rounded-lg">
              <Briefcase className="h-5 w-5" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white">{trades.length}</p>
        </div>

        {/* Open Trades Card */}
        <div className="bg-slate-800/50 backdrop-blur-md rounded-2xl p-6 border border-slate-700/50 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-slate-400 font-medium">Open Positions</h3>
            <div className="p-2 bg-blue-500/10 text-blue-400 rounded-lg">
              <Activity className="h-5 w-5" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white">{openTrades.length}</p>
        </div>

        {/* Closed Trades Card */}
        <div className="bg-slate-800/50 backdrop-blur-md rounded-2xl p-6 border border-slate-700/50 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-slate-400 font-medium">Closed Trades</h3>
            <div className="p-2 bg-purple-500/10 text-purple-400 rounded-lg">
              <TrendingUp className="h-5 w-5" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white">{closedTrades.length}</p>
        </div>

        {/* Total Profit Card */}
        <div className="bg-slate-800/50 backdrop-blur-md rounded-2xl p-6 border border-slate-700/50 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-slate-400 font-medium">Total Profit</h3>
            <div className="p-2 bg-emerald-500/10 text-emerald-400 rounded-lg">
              <DollarSign className="h-5 w-5" />
            </div>
          </div>
          <p className={`text-3xl font-bold ${totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            ${totalProfit.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Connected Accounts Node Graph Section */}
      <div className="mt-8 bg-slate-900/40 backdrop-blur-md rounded-3xl border border-slate-700/50 overflow-hidden shadow-2xl">
        <div className="px-6 py-5 border-b border-slate-700/50 flex justify-between items-center bg-slate-800/30">
          <h3 className="text-xl font-bold text-white tracking-wide">Network Graph</h3>
          <span className="bg-orange-500/20 text-orange-400 text-xs px-3 py-1 rounded-full border border-orange-500/20 font-semibold tracking-widest uppercase">
            {accounts.filter(a => new Date() - new Date(a.last_seen) < 120000).length} Online
          </span>
        </div>
        
        <div className="p-8 overflow-x-auto">
          <NodeGraph accounts={accounts} changeRole={changeRole} toggleMute={toggleMute} />
        </div>
      </div>

      <div className="mt-8 bg-slate-800/50 backdrop-blur-md rounded-2xl border border-slate-700/50 overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-700/50">
          <h3 className="text-lg font-semibold text-white">Recent Trades</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-800/30 text-slate-400 text-sm">
                <th className="px-6 py-4 font-medium">Account</th>
                <th className="px-6 py-4 font-medium">Ticket</th>
                <th className="px-6 py-4 font-medium">Symbol</th>
                <th className="px-6 py-4 font-medium">Type</th>
                <th className="px-6 py-4 font-medium">Volume</th>
                <th className="px-6 py-4 font-medium">Open Price</th>
                <th className="px-6 py-4 font-medium">Status</th>
                <th className="px-6 py-4 font-medium text-right">Profit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {trades.slice(0, 15).map((trade) => (
                <React.Fragment key={trade.id}>
                  <tr 
                    className={`transition-colors cursor-pointer ${expandedTradeId === trade.id ? 'bg-slate-800/60' : 'hover:bg-slate-800/40'}`}
                    onClick={() => setExpandedTradeId(expandedTradeId === trade.id ? null : trade.id)}
                  >
                    <td className="px-6 py-4 text-white font-medium">
                      {trade.account_login || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 text-slate-300 font-medium flex items-center gap-2">
                      {trade.strategy_data && (
                        expandedTradeId === trade.id ? <ChevronUp className="w-4 h-4 text-indigo-400" /> : <ChevronDown className="w-4 h-4 text-slate-500" />
                      )}
                      {trade.ticket}
                    </td>
                    <td className="px-6 py-4 text-white font-bold">{trade.symbol}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-md text-xs font-semibold ${trade.type.toLowerCase() === 'buy' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                        {trade.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-300">{trade.volume}</td>
                    <td className="px-6 py-4 text-slate-300">${trade.open_price}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-md text-xs font-semibold ${trade.status === 'Open' ? 'bg-blue-500/10 text-blue-400' : 'bg-slate-700 text-slate-300'}`}>
                        {trade.status}
                      </span>
                    </td>
                    <td className={`px-6 py-4 text-right font-bold ${Number(trade.profit) > 0 ? 'text-emerald-400' : Number(trade.profit) < 0 ? 'text-red-400' : 'text-slate-400'}`}>
                      {trade.status === 'Closed' ? `$${Number(trade.profit).toFixed(2)}` : '-'}
                    </td>
                  </tr>
                  
                  {/* Expanded Details Row */}
                  {expandedTradeId === trade.id && trade.strategy_data && (
                    <tr className="bg-slate-800/30">
                      <td colSpan="8" className="px-8 py-6">
                        <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
                          <h4 className="text-sm font-semibold text-indigo-400 mb-3 uppercase tracking-wider">Strategy Parameters at Entry</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                            {Object.entries(typeof trade.strategy_data === 'string' ? JSON.parse(trade.strategy_data) : trade.strategy_data).map(([key, value]) => (
                              <div key={key} className="bg-slate-800 rounded-lg p-3 border border-slate-700/30">
                                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">{key.replace('_', ' ')}</p>
                                <p className="text-sm font-bold text-white">{Number(value).toFixed(4)}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {trades.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-slate-400">
                    No trades found. Start trading to see history!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
